import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { UseFieldArrayAppend, UseFormSetValue } from 'react-hook-form';
import Select from 'react-select';
import { VinculoAssessorDefensorFormDto, PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import { validarVinculoAssessorDefensor } from '@/modules/people/presentation/forms/profiles/vinculo-assessor-defensor.utils';
import { ColaboradorEnum } from '@/modules/people/enums/tipo-vinculo.enum';
import { readPerson } from '@/modules/people/person.service';
import { BotaoAcaoPerfil } from '@/modules/people/presentation/components/BotaoAcaoPerfil';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import '@/modules/people/presentation/components/perfil-tabs-acoes.css';

interface AdicionarVinculoAssessorModalProps {
  show: boolean;
  handleHide: () => void;
  initialData: VinculoAssessorDefensorFormDto;
  itemIndex: number;
  existentes: VinculoAssessorDefensorFormDto[];
  appendItem: UseFieldArrayAppend<PersonFormDto, 'defensor.vinculosAssessores'>;
  setValue: UseFormSetValue<PersonFormDto>;
  onExcluir?: () => void;
}

type AssessorOption = { value: number; label: string };

const AdicionarVinculoAssessorModal: React.FC<AdicionarVinculoAssessorModalProps> = ({
  show,
  handleHide,
  initialData,
  itemIndex,
  existentes,
  appendItem,
  setValue,
  onExcluir,
}) => {
  const { register, handleSubmit, reset, watch, control, formState: { errors }, setError, clearErrors } =
    useForm<VinculoAssessorDefensorFormDto>();
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [opcoesAssessores, setOpcoesAssessores] = useState<AssessorOption[]>([]);
  const [carregandoAssessores, setCarregandoAssessores] = useState(false);
  const [modalExcluirShow, setModalExcluirShow] = useState(false);

  const dataFinalValue = watch('data_final');
  const dataFinalRegister = register('data_final', {
    setValueAs: (v) => (v === '' || v == null ? null : v),
  });

  useEffect(() => {
    if (!show) return;
    setCarregandoAssessores(true);
    readPerson({
      skip: 0,
      take: 9000,
      comColaborador: true,
      isColaborador: true,
      orderBy: 'ASC',
    })
      .then((result) => {
        const options = result[0]
          .filter((item) => item.colaborador && Number(item.colaborador.tipo_vinculo) === ColaboradorEnum.assessor)
          .map((item) => ({
            value: item.colaborador!.id,
            label: item.nome,
          }));
        setOpcoesAssessores(options);
      })
      .catch((error) => {
        console.warn('Erro ao carregar assessores:', error);
        setOpcoesAssessores([]);
      })
      .finally(() => setCarregandoAssessores(false));
  }, [show]);

  const onSubmit = (data: VinculoAssessorDefensorFormDto) => {
    clearErrors();
    setErroGeral(null);

    const opcao = opcoesAssessores.find((o) => o.value === Number(data.colaborador_id));
    const payload: VinculoAssessorDefensorFormDto = {
      ...initialData,
      ...data,
      colaborador_id: Number(data.colaborador_id),
      colaborador_nome: opcao?.label ?? data.colaborador_nome ?? initialData.colaborador_nome,
      data_final: data.data_final ? data.data_final : null,
    };

    const erro = validarVinculoAssessorDefensor(payload, existentes, itemIndex);
    if (erro) {
      if (erro.includes('final')) {
        setError('data_final', { type: 'validate', message: erro });
      } else if (erro.includes('inicial')) {
        setError('data_inicial', { type: 'validate', message: erro });
      } else if (erro.toLowerCase().includes('assessor')) {
        setError('colaborador_id', { type: 'validate', message: erro });
      } else {
        setErroGeral(erro);
      }
      return;
    }

    if (itemIndex >= 0) {
      setValue(`defensor.vinculosAssessores.${itemIndex}`, payload, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      appendItem(payload, { shouldFocus: false });
    }

    handleHide();
  };

  const handleSalvar = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit(onSubmit)();
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setModalExcluirShow(false);
    onExcluir?.();
    handleHide();
  };

  useEffect(() => {
    if (show) {
      setErroGeral(null);
      setModalExcluirShow(false);
      clearErrors();
      reset({
        id: initialData?.id,
        colaborador_id: initialData?.colaborador_id,
        colaborador_nome: initialData?.colaborador_nome ?? '',
        data_inicial: initialData?.data_inicial ?? '',
        data_final: initialData?.data_final ?? null,
      });
    }
  }, [show, initialData, reset, clearErrors]);

  const opcoesComAtual = useMemo(() => {
    if (
      initialData?.colaborador_id &&
      !opcoesAssessores.some((o) => o.value === Number(initialData.colaborador_id))
    ) {
      return [
        {
          value: Number(initialData.colaborador_id),
          label: initialData.colaborador_nome || `Assessor #${initialData.colaborador_id}`,
        },
        ...opcoesAssessores,
      ];
    }
    return opcoesAssessores;
  }, [opcoesAssessores, initialData]);

  return (
    <>
      <Modal show={show} onHide={handleHide} centered>
        <Modal.Header closeButton className="py-2 perfil-modal-header">
          <Modal.Title className="fs-6">
            {itemIndex >= 0 ? 'Editar vínculo de assessor' : 'Novo vínculo de assessor'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          {erroGeral && (
            <Alert variant="danger" className="py-2 px-2 small mb-2">
              {erroGeral}
            </Alert>
          )}
          <Row className="g-2">
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="mb-1 small">Assessor *</Form.Label>
                <Controller
                  name="colaborador_id"
                  control={control}
                  rules={{ required: 'Selecione o assessor' }}
                  render={({ field }) => (
                    <Select<AssessorOption>
                      classNamePrefix="react-select"
                      placeholder={carregandoAssessores ? 'Carregando…' : 'Selecione o assessor'}
                      isLoading={carregandoAssessores}
                      options={opcoesComAtual}
                      isClearable
                      value={opcoesComAtual.find((o) => o.value === Number(field.value)) ?? null}
                      onChange={(option) => field.onChange(option?.value ?? undefined)}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
                    />
                  )}
                />
                {errors.colaborador_id && (
                  <div className="invalid-feedback d-block">{errors.colaborador_id.message}</div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="mb-1 small">Início *</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  {...register('data_inicial', { required: 'Informe a data inicial' })}
                  isInvalid={!!errors.data_inicial}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.data_inicial?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="mb-1 small">Fim</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  name={dataFinalRegister.name}
                  ref={dataFinalRegister.ref}
                  onBlur={dataFinalRegister.onBlur}
                  onChange={dataFinalRegister.onChange}
                  value={dataFinalValue ?? ''}
                  isInvalid={!!errors.data_final}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.data_final?.message}
                </Form.Control.Feedback>
                {!errors.data_final && (
                  <Form.Text className="text-muted small">
                    Em branco = vínculo em aberto.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between">
          <div>
            {itemIndex >= 0 && onExcluir && (
              <BotaoAcaoPerfil tipo="excluir" onClick={() => setModalExcluirShow(true)} />
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleHide}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              className="perfil-modal-btn-salvar"
              onClick={handleSalvar}
            >
              Confirmar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ModalExcluir
        show={modalExcluirShow}
        handleHide={() => setModalExcluirShow(false)}
        handleExcluir={handleExcluir}
        mensagemPersonalizada="Deseja remover este vínculo de assessor?"
      />
    </>
  );
};

export default AdicionarVinculoAssessorModal;
